// src/components/site-editor/SiteEditorPreview.jsx
import React, { useRef, useEffect } from 'react';
import { useSiteEditor } from '../../hooks/useSiteEditor';

/**
 * Site Editor Preview Component
 *
 * Renders the site preview in an iframe for complete CSS isolation
 *
 * The iframe loads the preview site (from preview_url) with query params
 * to fetch draft content. This provides true CSS isolation - preview site has
 * its own build with no dashboard contamination.
 *
 * Preview URL:
 * - Development: Falls back to http://localhost:3001 if preview_url not set
 * - Production: Uses preview_url from database (e.g., https://harboroak.daybloom.io)
 *
 * Real-time Updates:
 * - Uses postMessage to send content updates to iframe
 * - Preview site listens and updates its React state
 * - No page reload needed - instant preview updates
 */
const SiteEditorPreview = () => {
  const { currentSite, draftContent } = useSiteEditor();
  const iframeRef = useRef(null);

  // Get token directly from localStorage since AuthContext doesn't expose it
  const token = localStorage.getItem('access_token');

  // Determine preview base URL (use preview_url from database, fallback to localhost for dev)
  const previewBaseUrl = currentSite?.preview_url || 'http://localhost:3001';

  // Send updated content to iframe when draftContent changes
  useEffect(() => {
    console.log('🎬 SiteEditorPreview: draftContent changed', {
      hasIframe: !!iframeRef.current,
      hasDraftContent: !!draftContent,
      previewBaseUrl
    });

    if (iframeRef.current && draftContent) {
      // Small delay to ensure iframe is loaded
      const timer = setTimeout(() => {
        try {
          // Extract origin from preview URL for postMessage security
          const previewOrigin = new URL(previewBaseUrl).origin;

          iframeRef.current.contentWindow.postMessage(
            {
              type: 'UPDATE_CONTENT',
              content: draftContent
            },
            previewOrigin // Target origin for security
          );
          console.log(`📤 Sent content update to iframe (${previewOrigin})`);
        } catch (error) {
          console.error('Failed to send postMessage:', error);
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [draftContent, previewBaseUrl]);

  if (!currentSite) {
    return (
      <div className="flex items-center justify-center h-full bg-white">
        <p className="text-muted-foreground">Loading site preview...</p>
      </div>
    );
  }

  if (!token) {
    return (
      <div className="flex items-center justify-center h-full bg-white">
        <p className="text-muted-foreground">Authentication required...</p>
      </div>
    );
  }

  // Build preview URL with query params
  const previewUrl = `${previewBaseUrl}?siteId=${currentSite.id}&token=${token}`;

  return (
    <iframe
      ref={iframeRef}
      src={previewUrl}
      className="w-full h-full border-0"
      title="Site Preview"
      sandbox="allow-scripts allow-same-origin"
    />
  );
};

export default SiteEditorPreview;
